import { useReducedMotion } from 'motion/react'
import { Link } from 'react-router-dom'
import { ParticleSignal } from '@/components/particle-signal'
import { PillCTA } from '@/components/pill-cta'
import { Reveal } from '@/components/reveal'
import { ScatterDiagram } from '@/components/scatter-diagram'
import { StatRow } from '@/components/stat-row'
import { usePageMeta } from '@/lib/use-page-meta'

const STEPS = [
  { n: 'One', text: 'Reach out. Click Get Started or just call.' },
  { n: 'Two', text: 'We hop on a quick call to talk about your business.' },
  { n: 'Three', text: "Once we're on the same page, I'll send over a simple payment link, made just for you." },
  { n: 'Four', text: 'Your ads go live within 7 days.' },
]

const METRICS = [
  {
    label: 'Cost per result',
    direction: 'down' as const,
    caption: "What you're actually paying to get one lead, watched every week.",
    points: [
      { x: 4, y: 12 }, { x: 20, y: 22 }, { x: 38, y: 18 },
      { x: 56, y: 34 }, { x: 74, y: 40 }, { x: 96, y: 52 },
    ],
  },
  {
    label: 'Click-through rate',
    direction: 'up' as const,
    caption: 'How many people who see the ad actually stop and click.',
    points: [
      { x: 4, y: 50 }, { x: 22, y: 44 }, { x: 40, y: 46 },
      { x: 58, y: 30 }, { x: 78, y: 22 }, { x: 96, y: 10 },
    ],
  },
  {
    label: 'Return on ad spend',
    direction: 'up' as const,
    caption: 'Revenue back for every dollar spent running the ads.',
    points: [
      { x: 4, y: 46 }, { x: 24, y: 48 }, { x: 44, y: 32 },
      { x: 62, y: 34 }, { x: 80, y: 16 }, { x: 96, y: 8 },
    ],
  },
]

export function HomePage() {
  usePageMeta(
    'Flowscale — Facebook, Instagram + Google ads, run for you',
    "You'll know our ads work when you spend one dollar to make two. That's the return I optimize for.",
  )
  const reduceMotion = useReducedMotion()

  return (
    <>
      {/* Hero -- a generative signal instead of a photo or an illustration,
          because that's the actual product: a noisy account turned into
          one clean line. */}
      <section className="px-4 pb-4 pt-16 min-[900px]:px-8 min-[900px]:pt-20">
        <div className="mx-auto max-w-[1400px]">
          <p className="mb-6 text-meta uppercase tracking-[0.25em] text-n-500">
            Facebook, Instagram + Google ads, run for you
          </p>
          <h1 className="max-w-[22ch] font-display text-[clamp(38px,8vw,84px)] italic leading-[1.02] tracking-[-0.015em] text-pretty">
            You'll know our ads work when you spend one dollar to make two.
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-8">
            <p className="max-w-[38ch] text-lead leading-[1.42] text-pretty text-n-500">
              That's the return I optimize for. Real revenue, not clicks or impressions.
            </p>
            <PillCTA to="/contact">Get Started</PillCTA>
          </div>
        </div>
      </section>

      <section className="px-4 min-[900px]:px-8">
        <div className="mx-auto max-w-[1400px] border-y border-n-200">
          <ParticleSignal reduceMotion={!!reduceMotion} />
        </div>
      </section>

      {/* Statement -- the problem/coordinator beat from
          generalintelligencecompany.com, ported to what an ad account
          actually looks like from the inside. */}
      <section className="px-4 py-20 min-[900px]:px-8 min-[900px]:py-32">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <p className="max-w-[22ch] font-display text-[clamp(28px,5vw,52px)] leading-[1.14] tracking-[-0.01em] text-pretty">
              Most ad budgets are half wasted.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 max-w-[20ch] font-display text-[clamp(28px,5vw,52px)] italic leading-[1.14] tracking-[-0.01em] text-flow text-pretty">
              I find the half that isn't.
            </p>
          </Reveal>

          <div className="mt-16 grid gap-10 min-[900px]:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] min-[900px]:items-center min-[900px]:gap-16">
            <Reveal delay={0.15}>
              <ScatterDiagram className="h-auto w-full max-w-[360px]" reduceMotion={!!reduceMotion} />
            </Reveal>
            <Reveal delay={0.2}>
              <div className="max-w-[48ch]">
                <p className="text-meta uppercase tracking-[0.17em] text-n-500">How the budget actually moves</p>
                <p className="mt-3 text-lead leading-[1.5] text-pretty">
                  Everything starts as a small, cheap test. Underperformers get cut fast. Winners get the rest of
                  the budget.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Results -- a divided data row, not a grid of cards. */}
      <section id="dashboard" className="border-t border-n-200 bg-n-300 px-4 py-20 min-[900px]:px-8 min-[900px]:py-28">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <h2 className="mb-3 text-meta uppercase tracking-[0.17em] text-n-500">
              What's actually in the dashboard
            </h2>
            <p className="mb-4 max-w-[46ch] text-lead leading-[1.4] text-pretty">
              Every account boils down to three numbers. Hover one to see the trend.
            </p>
          </Reveal>
          <div>
            {METRICS.map((m, i) => (
              <Reveal key={m.label} delay={i * 0.08}>
                <StatRow {...m} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process -- plain divided rows, ordinal words instead of digits. */}
      <section id="next" className="bg-canvas px-4 py-20 min-[900px]:px-8 min-[900px]:py-28">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <h2 className="mb-10 text-meta uppercase tracking-[0.17em] text-n-500">What happens next</h2>
          </Reveal>
          <ol className="grid list-none gap-0 p-0">
            {STEPS.map((step, i) => (
              // Reveal renders its own wrapping div; putting it inside <li>
              // instead of around it keeps <li> a direct child of <ol>, which
              // axe-core (and every screen reader) requires for list
              // semantics -- a <div> between them breaks list navigation.
              <li key={step.n} className="border-t border-n-200">
                <Reveal
                  delay={i * 0.06}
                  className="grid grid-cols-1 gap-2 py-7 min-[900px]:grid-cols-[10ch_minmax(0,1fr)] min-[900px]:gap-10 min-[900px]:py-9"
                >
                  <span className="font-display text-lead italic text-flow">{step.n}</span>
                  <div>
                    <span className="block max-w-[46ch] text-lead leading-[1.4]">{step.text}</span>
                    {step.n === 'Four' && (
                      <span className="mt-1 block text-body text-n-500">
                        See the{' '}
                        <Link
                          to="/terms-of-service"
                          className="text-ink underline decoration-n-200 underline-offset-4 hover:text-flow hover:decoration-flow"
                        >
                          Terms page
                        </Link>{' '}
                        for when results typically show.
                      </span>
                    )}
                  </div>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Websites are a separate, optional service -- moved here from a
          dedicated What We Do section that visitors weren't reaching. */}
      <section className="border-t border-n-200 bg-n-300 px-4 py-20 min-[900px]:px-8 min-[900px]:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-6 min-[900px]:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] min-[900px]:items-center min-[900px]:gap-20">
          <Reveal>
            <p className="text-meta uppercase tracking-[0.17em] text-n-500">Also available</p>
            <p className="mt-3 max-w-[30ch] font-display text-[clamp(26px,4vw,40px)] leading-[1.2] tracking-[-0.01em] text-pretty">
              Ads work harder when the page they land on can close the deal.
            </p>
            <p className="mt-2 max-w-[30ch] font-display text-[clamp(26px,4vw,40px)] italic leading-[1.2] tracking-[-0.01em] text-flow text-pretty">
              If yours doesn't, I can build one.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col items-start gap-4">
              <p className="max-w-[36ch] text-body leading-[1.55] text-pretty text-n-500">
                Priced and scoped separately from ad management. Recommended when it'll actually move the needle,
                never bundled in by default.
              </p>
              <Link
                to="/contact"
                data-cursor="link"
                className="text-body text-ink underline decoration-n-200 underline-offset-4 hover:text-flow hover:decoration-flow"
              >
                Ask about a website
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Closing -- flat, no ambient glow. The statement carries it. */}
      <section id="cta" className="border-t border-n-200 px-4 py-20 min-[900px]:px-8 min-[900px]:py-32">
        <div className="mx-auto flex max-w-[1000px] flex-col items-center gap-8 text-center">
          <Reveal>
            <p className="font-display text-[clamp(30px,5.5vw,56px)] italic leading-[1.1] tracking-[-0.01em] text-pretty">
              Fewer moving parts. Better numbers.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-[46ch] text-lead leading-[1.5] text-pretty text-n-500">
              You tell me what the business needs, I run the ads, you see what's working.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <PillCTA to="/contact" big>
              Get Started
            </PillCTA>
          </Reveal>
        </div>
      </section>
    </>
  )
}
